import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { TaskDatePicker } from "./TaskDatePicker";
import { TaskTimePicker } from "./TaskTimePicker";

export interface SubtaskMemberOption {
  id: string;
  display_name: string | null;
}

interface SubtaskConfigFieldsProps {
  description: string;
  onDescriptionChange: (value: string) => void;
  /** Disparado ao sair do campo de descrição (usado para persistir na edição). */
  onDescriptionBlur?: () => void;
  assigneeIds: string[];
  onAssigneeIdsChange: (value: string[]) => void;
  date: string;
  onDateChange: (value: string) => void;
  time: string;
  onTimeChange: (value: string) => void;
  isAdmin: boolean;
  members: SubtaskMemberOption[];
  minDate?: string;
  idPrefix: string;
  /** Mensagem de erro de validação da data (ex.: posterior à tarefa-pai). */
  dateError?: string | null;
}

/**
 * Campos de configuração de uma subtarefa (descrição, responsáveis, data e horário).
 * Compartilhado entre o modo rascunho (criação) e o modo persistido (edição),
 * exibido no estado expandido das linhas estilo Asana.
 */
export function SubtaskConfigFields({
  description,
  onDescriptionChange,
  onDescriptionBlur,
  assigneeIds,
  onAssigneeIdsChange,
  date,
  onDateChange,
  time,
  onTimeChange,
  isAdmin,
  members,
  minDate,
  idPrefix,
  dateError,
}: SubtaskConfigFieldsProps) {
  const toggleAssignee = (memberId: string, checked: boolean) => {
    if (checked) {
      if (!assigneeIds.includes(memberId)) {
        onAssigneeIdsChange([...assigneeIds, memberId]);
      }
      return;
    }
    const next = assigneeIds.filter((id) => id !== memberId);
    if (next.length > 0) onAssigneeIdsChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-description`} className="text-xs text-muted-foreground">
          Descrição
        </Label>
        <Textarea
          id={`${idPrefix}-description`}
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          onBlur={onDescriptionBlur}
          rows={2}
          placeholder="O que envolve esta subtarefa?"
          className="resize-none text-sm"
        />
      </div>
      {isAdmin && (
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Responsáveis</Label>
          <div className="max-h-40 space-y-2 overflow-y-auto rounded-md border p-2">
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-2">
                <Checkbox
                  id={`${idPrefix}-assignee-${m.id}`}
                  checked={assigneeIds.includes(m.id)}
                  onCheckedChange={(checked) => toggleAssignee(m.id, checked === true)}
                />
                <label
                  htmlFor={`${idPrefix}-assignee-${m.id}`}
                  className="cursor-pointer text-sm"
                >
                  {m.display_name || "(sem nome)"}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-date`} className="text-xs text-muted-foreground">
            Data
          </Label>
          <TaskDatePicker
            id={`${idPrefix}-date`}
            value={date}
            onChange={onDateChange}
            minDate={minDate}
            clearable
            placeholder="Sem data"
            className={dateError ? "h-9 border-destructive" : "h-9"}
          />
          {dateError && (
            <p className="text-xs text-destructive">{dateError}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-time`} className="text-xs text-muted-foreground">
            Horário
          </Label>
          <TaskTimePicker
            id={`${idPrefix}-time`}
            value={time}
            disabled={!date}
            onChange={onTimeChange}
            clearable
            className="h-9"
          />
        </div>
      </div>
    </div>
  );
}
